import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight, MailCheck, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { getShopifyEmbeddedContext, buildEmbeddedAppPath } from "@/lib/shopifyEmbedded";
import {
  checkShopifyAccountContext,
  requestShopifyAccountSwitchOtp,
  verifyShopifyAccountSwitchOtp,
} from "@/lib/shopifyAccountSwitch";

interface ShopifyAccountGuardProps {
  children: React.ReactNode;
}

type GuardState = "loading" | "connected" | "unconnected" | "mismatch" | "requesting" | "verify" | "error";

export default function ShopifyAccountGuard({ children }: ShopifyAccountGuardProps) {
  const { user, loading: authLoading, signOut } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<GuardState>("loading");
  const [storeName, setStoreName] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const context = getShopifyEmbeddedContext();
  const shopDomain = context.embedded ? context.shop : null;

  useEffect(() => {
    let cancelled = false;

    if (authLoading || !shopDomain) {
      if (!authLoading && !shopDomain) setState("connected");
      return () => { cancelled = true; };
    }

    setState("loading");
    setError("");
    checkShopifyAccountContext(shopDomain)
      .then((result) => {
        if (cancelled) return;
        setStoreName(result.storeName || shopDomain);
        setMaskedEmail(result.maskedEmail || "");
        setState(result.status === "mismatch" ? "mismatch" : result.status === "unconnected" ? "unconnected" : "connected");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : "Unable to validate this Shopify account");
        setState("error");
      });

    return () => { cancelled = true; };
  }, [authLoading, shopDomain, user?.id, location.pathname]);

  if (!context.embedded || !shopDomain || state === "connected" || state === "unconnected") {
    return <>{children}</>;
  }

  const requestCode = async () => {
    setState("requesting");
    setError("");
    try {
      const result = await requestShopifyAccountSwitchOtp(shopDomain);
      setMaskedEmail(result.maskedEmail || "the connected account email");
      setCode("");
      setState("verify");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to send the sign-in code");
      setState("mismatch");
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) return;
    setState("requesting");
    setError("");
    try {
      const result = await verifyShopifyAccountSwitchOtp(
        shopDomain,
        code,
        new URL(buildEmbeddedAppPath("/merchant-login"), window.location.origin).toString(),
      );
      if (!result.actionLink) throw new Error("The account sign-in link was not created");
      localStorage.removeItem("active_merchant_id");
      await signOut();
      window.location.assign(result.actionLink);
    } catch (verifyError) {
      setError(verifyError instanceof Error ? verifyError.message : "Unable to verify the sign-in code");
      setState("verify");
    }
  };

  if (state === "loading") {
    return <GuardMessage title="Checking store connection" message="Confirming the Splitkart account connected to this Shopify store." />;
  }

  if (state === "error") {
    return <GuardMessage title="Store connection could not be checked" message={error || "Refresh the page and try again."} error />;
  }

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            {state === "verify" ? <MailCheck className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
          </div>
          <CardTitle className="text-2xl">Switch to the connected account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-center">
          <p className="text-muted-foreground">
            This Shopify store{storeName ? ` (${storeName})` : ""} is connected to a different Splitkart account.
            Your current account has not been shown store data.
          </p>
          {state === "verify" ? (
            <>
              <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to {maskedEmail}.</p>
              <InputOTP maxLength={6} value={code} onChange={setCode} className="justify-center">
                <InputOTPGroup>
                  {Array.from({ length: 6 }, (_, index) => <InputOTPSlot key={index} index={index} />)}
                </InputOTPGroup>
              </InputOTP>
              <Button className="w-full" disabled={code.length !== 6 || state === "requesting"} onClick={verifyCode}>
                Verify and switch account <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button type="button" className="text-sm text-primary underline" onClick={requestCode}>Send a new code</button>
            </>
          ) : (
            <>
              <Button className="w-full" disabled={state === "requesting"} onClick={requestCode}>
                {state === "requesting" ? "Sending code..." : "Send OTP to connected account"}
              </Button>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">You must have access to the connected account email to switch accounts.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GuardMessage({ title, message, error = false }: { title: string; message: string; error?: boolean }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <AlertCircle className={`mx-auto mb-4 h-10 w-10 ${error ? "text-destructive" : "text-primary"}`} />
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}
